/**
 * Utility to invalidate open counts cache after mutations.
 *
 * Use this after any mutation that affects defect/inspection status or assignment.
 * This ensures sidebar badges update immediately without waiting for realtime events.
 *
 * Pattern: Local Mutation + Realtime
 * - Local mutations call these functions for immediate feedback
 * - Realtime subscriptions handle updates from other users
 *
 * @see .lovable/memory/technical/realtime-configuration.md
 */
import { QueryClient } from "@tanstack/react-query";
import { openCountsKeys } from "@/hooks/useOpenCounts";
import { connectivityService } from "@/lib/connectivityService";

/**
 * Invalidate all open counts (defects, inspections, tasks).
 * Use when a mutation affects both defects and inspections.
 */
export function invalidateOpenCounts(
  queryClient: QueryClient,
  tenantId: string | null,
  userId?: string,
) {
  if (!connectivityService.isOnline) return;

  queryClient.invalidateQueries({ queryKey: openCountsKeys.defects(tenantId) });
  queryClient.invalidateQueries({ queryKey: openCountsKeys.inspections(tenantId) });

  if (userId) {
    queryClient.invalidateQueries({ queryKey: openCountsKeys.tasks(userId, tenantId) });
  }
}

/**
 * Invalidate defect-related counts.
 * Use after mutations that affect defects (create, update status, assign, delete).
 */
export function invalidateDefectCounts(
  queryClient: QueryClient,
  tenantId: string | null,
  userId?: string,
) {
  if (!connectivityService.isOnline) return;

  queryClient.invalidateQueries({ queryKey: openCountsKeys.defects(tenantId) });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: openCountsKeys.tasks(userId, tenantId) });
  }
}

/**
 * Invalidate inspection-related counts.
 * Use after mutations that affect inspections (create, update status, assign, complete).
 */
export function invalidateInspectionCounts(
  queryClient: QueryClient,
  tenantId: string | null,
  userId?: string,
) {
  if (!connectivityService.isOnline) return;

  queryClient.invalidateQueries({ queryKey: openCountsKeys.inspections(tenantId) });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: openCountsKeys.tasks(userId, tenantId) });
  }
}
